// shell/components/entity-view/types.ts

import type { PaneInstanceSpec } from '../../core/types';

// ============================================================================
// Entity View Mode
// ============================================================================

/**
 * Operating mode for EntityView.
 * - 'view': Read-only display of entity data
 * - 'edit': Edit form for existing entity
 * - 'new': Create form for new entity
 */
export type EntityViewMode = 'view' | 'edit' | 'new';

// ============================================================================
// Base Field Types
// ============================================================================

/**
 * Base properties shared by all field definitions.
 * @template T - The entity model type
 */
interface FieldBase<T> {
    /** Property key on the model, or a custom key for computed fields */
    key: keyof T | string;
    /** Display label for the field */
    label: string;
    /** Optional visibility predicate — if provided and returns false, the field is not rendered. */
    visible?: (model: T) => boolean;
}

/**
 * Extended base for form fields with common input properties.
 * @template T - The entity model type
 */
interface FormFieldBase<T> extends FieldBase<T> {
    /** Whether the field is required */
    required?: boolean;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Help text displayed below the field */
    helpText?: string;
}

// ============================================================================
// View Field Types (display-oriented)
// ============================================================================

/**
 * Text display field.
 */
export interface TextViewField<T> extends FieldBase<T> {
    type: 'text';
    /** Custom formatter for the displayed value */
    format?: (value: string, model: T) => string;
}

/**
 * Number display field.
 */
export interface NumberViewField<T> extends FieldBase<T> {
    type: 'number';
    /** Custom formatter (e.g., currency, percentage) */
    format?: (value: number, model: T) => string;
}

/**
 * Date display field.
 */
export interface DateViewField<T> extends FieldBase<T> {
    type: 'date';
    /** Display format: 'date' (default), 'datetime', or 'relative' */
    format?: 'date' | 'datetime' | 'relative';
}

/**
 * Boolean display field.
 */
export interface BooleanViewField<T> extends FieldBase<T> {
    type: 'boolean';
    /** Label for true value (default: 'Yes') */
    trueLabel?: string;
    /** Label for false value (default: 'No') */
    falseLabel?: string;
}

/**
 * Badge display field - colored pill/chip.
 */
export interface BadgeViewField<T> extends FieldBase<T> {
    type: 'badge';
    /** Map value to background color */
    colorMap?: Record<string, string>;
    /** Map value to display label */
    labelMap?: Record<string, string>;
}

/**
 * Entity link field - clickable link that opens another pane.
 */
export interface EntityLinkViewField<T> extends FieldBase<T> {
    type: 'entity-link';
    /** Property key containing the display name */
    displayKey: keyof T;
    /** Function to generate the pane spec for navigation */
    link: (id: unknown, model: T) => PaneInstanceSpec;
}

/**
 * Tag list display field.
 */
export interface TagListViewField<T> extends FieldBase<T> {
    type: 'tag-list';
    /** If value is array of objects, which property to display */
    displayKey?: keyof T;
    /** Function to determine tag color */
    colorFn?: (tag: string, model: T) => string;
}

/**
 * File list display field.
 */
export interface FilesViewField<T> extends FieldBase<T> {
    type: 'files';
    /** Whether to show file previews */
    showPreview?: boolean;
    /** Custom download handler */
    onDownload?: (file: FileInfo) => Promise<void>;
}

/**
 * HTML content display field.
 */
export interface HtmlViewField<T> extends FieldBase<T> {
    type: 'html';
    /** Whether to sanitize HTML (default: true) */
    sanitize?: boolean;
}

/**
 * Custom view field with factory pattern for specialized rendering.
 *
 * Use this for complex visualizations (charts, maps, etc.) that need
 * lifecycle management. The `create` factory is called once when the
 * field is rendered, returning a controller for updates and cleanup.
 *
 * @example
 * ```typescript
 * {
 *     type: 'custom',
 *     key: 'salesChart',
 *     label: 'Sales Trend',
 *     create: (container) => {
 *         const chart = new Chart(container);
 *         return {
 *             render: (value) => chart.update(value),
 *             dispose: () => chart.destroy()
 *         };
 *     }
 * }
 * ```
 */
export interface CustomViewField<T> extends FieldBase<T> {
    type: 'custom';
    /** Custom configuration (no intellisense - use typed helper for reusable fields) */
    options?: Record<string, unknown>;
    /** Factory function that creates the field controller */
    create: (container: HTMLElement) => CustomViewFieldController<T>;
}

/**
 * Controller returned by custom view field factory.
 */
export interface CustomViewFieldController<T> {
    /** Render or update the field with new value */
    render: (value: unknown, model: T) => void;
    /** Cleanup resources (event listeners, timers, etc.) */
    dispose?: () => void;
}

/**
 * Discriminated union of all view field types.
 * @template T - The entity model type
 */
export type ViewFieldDef<T> =
    | TextViewField<T>
    | NumberViewField<T>
    | DateViewField<T>
    | BooleanViewField<T>
    | BadgeViewField<T>
    | EntityLinkViewField<T>
    | TagListViewField<T>
    | FilesViewField<T>
    | HtmlViewField<T>
    | CustomViewField<T>;

// ============================================================================
// Form Field Types (input-oriented)
// ============================================================================

/**
 * Text input field.
 */
export interface TextFormField<T> extends FormFieldBase<T> {
    type: 'text';
    /** Maximum character length */
    maxLength?: number;
    /** Placeholder text */
    placeholder?: string;
    /** Render as password input */
    password?: boolean;
    /** Render as multiline textarea */
    multiline?: boolean;
    /** Number of rows for textarea (default: 3) */
    rows?: number;
    /** Custom validation function */
    validate?: (value: string, model: T) => boolean | string;
}

/**
 * Number input field.
 */
export interface NumberFormField<T> extends FormFieldBase<T> {
    type: 'number';
    /** Minimum value */
    min?: number;
    /** Maximum value */
    max?: number;
    /** Step increment */
    step?: number;
    /** Placeholder text */
    placeholder?: string;
    /** Custom validation function */
    validate?: (value: number, model: T) => boolean | string;
}

/**
 * Boolean input field (checkbox).
 */
export interface BooleanFormField<T> extends FormFieldBase<T> {
    type: 'boolean';
    /** Label displayed next to the checkbox */
    checkboxLabel?: string;
}

/**
 * Date input field.
 */
export interface DateFormField<T> extends FormFieldBase<T> {
    type: 'date';
    /** Include time picker */
    withTime?: boolean;
    /** Minimum selectable date */
    minDate?: Date;
    /** Maximum selectable date */
    maxDate?: Date;
}

/**
 * Select dropdown field.
 * @template V - The option value type (default: string)
 */
export interface SelectFormField<T, V = string> extends FormFieldBase<T> {
    type: 'select';
    /** Available options */
    options: SelectOption<V>[];
    /** Placeholder text when no selection */
    placeholder?: string;
    /** Show prev/next nudge buttons flanking the select (default: false) */
    nudge?: boolean;
}

/**
 * Option for select dropdown.
 */
export interface SelectOption<V = string> {
    key: V;
    label: string;
}

/**
 * Entity search field with async options.
 */
export interface EntitySearchFormField<T> extends FormFieldBase<T> {
    type: 'entity-search';
    /** Async function to fetch options based on search query */
    fetchOptions: (query: string) => Promise<SelectOption[]>;
    /** Placeholder text */
    placeholder?: string;
    /** Allow multiple selections */
    multiple?: boolean;
}

/**
 * Checkbox group field — pick any subset of a fixed option set.
 *
 * Optionally renders a leading "any" checkbox that represents the
 * "no restriction" state: checking it clears all specific selections
 * and yields an empty array; checking any specific option unchecks
 * "any". An empty value array shows "any" as checked.
 *
 * Value type: `string[]` (the `key` of each checked option).
 * @template V - The option value type (default: string)
 */
export interface CheckboxGroupFormField<T, V = string> extends FormFieldBase<T> {
    type: 'checkbox-group';
    /** Available options */
    options: SelectOption<V>[];
    /** Show an "any — no restriction" checkbox that maps to empty array. Default: true. */
    allowAny?: boolean;
    /** Label for the "any" checkbox. Default: 'any — no restriction'. */
    anyLabel?: string;
}

/**
 * Tag input field.
 */
export interface TagInputFormField<T> extends FormFieldBase<T> {
    type: 'tag-input';
    /** Placeholder text for the input (default: 'Add tag...') */
    placeholder?: string;
    /** Async function to fetch tag suggestions */
    fetchSuggestions?: (query: string) => Promise<SelectOption[]>;
    /** Allow creating custom tags not in suggestions */
    allowCustom?: boolean;
    /** Maximum number of tags */
    maxTags?: number;
    /** Stack the tags one per line instead of wrapping horizontally. Useful for
     *  long values (paths, rule strings) that read better as a vertical list. */
    vertical?: boolean;
    /** Keep tags alphabetically ordered (case-insensitive) as they are added, so
     *  the list stays sorted rather than growing in insertion order. Opt-in; the
     *  saved value is the sorted array. Default false (insertion order). */
    sorted?: boolean;
}

/**
 * File upload field.
 */
export interface FileInputFormField<T> extends FormFieldBase<T> {
    type: 'file-input';
    /** Accepted file types (e.g., 'image/*,.pdf') */
    accept?: string;
    /** Allow multiple file selection */
    multiple?: boolean;
    /** Maximum number of files */
    maxFiles?: number;
    /** Maximum file size in bytes */
    maxSize?: number;
    /** Show file previews */
    showPreview?: boolean;
}

/**
 * Rich text editor field.
 */
export interface RichTextFormField<T> extends FormFieldBase<T> {
    type: 'richtext';
    /** Placeholder text */
    placeholder?: string;
}

/**
 * Unit options for a duration field.
 */
export type DurationUnit = 'days' | 'months' | 'years';

/**
 * Bound value of a {@link DurationFormField}: a `{ unit, amount }`
 * pair, or `null` when the field is configured with `allowNever` and
 * the user picked the "never" option.
 */
export interface DurationValue {
    unit: DurationUnit;
    amount: number;
}

/**
 * Compound duration input — a unit selector tightly paired with a
 * narrow numeric amount. Picking the optional "never" option (when
 * `allowNever` is true) hides the amount and yields `null` as the
 * field value, modelling "no expiration" / "indefinite".
 *
 * The field is laid out as one column in the surrounding entity-view
 * row; the renderer puts the unit on the left and the amount on the
 * right with a tight gap, so callers should give it a single layout
 * slot (e.g., `['expires']`) rather than splitting into two fields.
 */
export interface DurationFormField<T> extends FormFieldBase<T> {
    type: 'duration';
    /** Which units to offer in the selector. Default: ['days','months','years']. */
    units?: DurationUnit[];
    /** Render a "never" option that yields `null`. Default: false. */
    allowNever?: boolean;
    /** Label for the "never" option. Default: `'never'`. */
    neverLabel?: string;
    /** Minimum amount (per unit). Default: 1. */
    min?: number;
    /** Maximum amount (per unit). Default: 999. */
    max?: number;
}

/**
 * Slider (range) input field.
 */
export interface SliderFormField<T> extends FormFieldBase<T> {
    type: 'slider';
    /** Minimum value */
    min: number;
    /** Maximum value */
    max: number;
    /** Step increment (default: 1) */
    step?: number;
}

/**
 * Custom form field with factory pattern.
 *
 * Use this for specialized inputs that need lifecycle management.
 * The `create` factory is called once, returning a controller for
 * value access and cleanup.
 *
 * @example
 * ```typescript
 * {
 *     type: 'custom',
 *     key: 'colorPicker',
 *     label: 'Theme Color',
 *     create: (container, onChange) => {
 *         const input = document.createElement('input');
 *         input.type = 'color';
 *         container.appendChild(input);
 *         input.addEventListener('input', () => onChange(input.value));
 *         return {
 *             render: (value) => { input.value = value ?? '#000000'; },
 *             getValue: () => input.value,
 *             focus: () => input.focus(),
 *             dispose: () => { }
 *         };
 *     }
 * }
 * ```
 */
export interface CustomFormField<T> extends FormFieldBase<T> {
    type: 'custom';
    /** Custom configuration (no intellisense - use typed helper for reusable fields) */
    options?: Record<string, unknown>;
    /** Factory function that creates the field controller */
    create: (
        container: HTMLElement,
        onChange: (value: unknown) => void
    ) => CustomFormFieldController;
}

/**
 * Controller returned by custom form field factory.
 */
export interface CustomFormFieldController {
    /** Render or update the field with new value */
    render: (value: unknown) => void;
    /** Get the current field value */
    getValue: () => unknown;
    /** Validate the field (return true if valid, string for error message) */
    validate?: () => Promise<boolean | string>;
    /** Focus the field */
    focus?: () => void;
    /** Cleanup resources */
    dispose?: () => void;
}

/**
 * Discriminated union of all form field types.
 * @template T - The entity model type
 */
export type FormFieldDef<T> =
    | TextFormField<T>
    | NumberFormField<T>
    | BooleanFormField<T>
    | DateFormField<T>
    | SelectFormField<T>
    | CheckboxGroupFormField<T>
    | DurationFormField<T>
    | SliderFormField<T>
    | EntitySearchFormField<T>
    | TagInputFormField<T>
    | FileInputFormField<T>
    | RichTextFormField<T>
    | CustomFormField<T>;

// ============================================================================
// Layout Types
// ============================================================================

/**
 * Layout row - array of field keys to display in a single row.
 * Fields in the same row share horizontal space equally.
 */
export type LayoutRow<T> = (keyof T | string)[];

/**
 * Section header in layout.
 */
export interface LayoutHeader {
    /** Header text */
    header: string;
    /** Optional icon name */
    icon?: string;
}

/**
 * Custom layout element with render function.
 */
export interface LayoutCustom<T> {
    /** Render function for custom content */
    custom: (container: HTMLElement, model: T) => void;
}

/**
 * Collapsible section that wraps child layout elements.
 */
export interface LayoutSection<T> {
    /** Section header text (shown as toggle) */
    section: string;
    /** Whether the section is collapsed by default */
    collapsed?: boolean;
    /** Child layout elements inside this section */
    children: LayoutElement<T>[];
}

/**
 * Discriminated union of layout elements.
 * @template T - The entity model type
 */
export type LayoutElement<T> = LayoutRow<T> | LayoutHeader | LayoutCustom<T> | LayoutSection<T>;

/**
 * Complete layout definition - array of layout elements.
 * @template T - The entity model type
 *
 * @example
 * ```typescript
 * const layout: LayoutDef<Person> = [
 *     { header: 'Basic Information', icon: 'user' },
 *     ['name', 'email'],     // Two fields in one row
 *     ['age', 'active'],
 *     { header: 'Organization' },
 *     ['department'],        // Single field takes full width
 *     ['manager'],
 * ];
 * ```
 */
export type LayoutDef<T> = LayoutElement<T>[];

// ============================================================================
// View and Form Configuration
// ============================================================================

/**
 * Configuration for view mode display.
 * @template T - The entity model type
 */
export interface ViewConfig<T> {
    /** Field definitions for view mode */
    fields: ViewFieldDef<T>[];
    /** Layout definition */
    layout: LayoutDef<T>;
}

/**
 * Configuration for form mode (edit/new).
 * @template T - The entity model type
 */
export interface FormConfig<T> {
    /** Field definitions for form mode */
    fields: FormFieldDef<T>[];
    /** Layout definition */
    layout: LayoutDef<T>;
}

// ============================================================================
// EntityView Configuration
// ============================================================================

/**
 * Complete configuration for EntityView component.
 * @template T - The entity model type
 */
export interface EntityViewConfig<T> {
    /** Container element to render into */
    container: HTMLElement;

    /** Async function to load the entity model */
    getModel: () => Promise<T>;

    /** Initial operating mode */
    initialMode: EntityViewMode;

    /** View mode configuration */
    view: ViewConfig<T>;

    /** Edit mode configuration */
    /** Edit mode configuration. Optional for view-only entities. */
    form?: FormConfig<T>;

    /** New mode configuration (defaults to form if not provided) */
    newForm?: FormConfig<T>;

    // -------------------------------------------------------------------------
    // CRUD Callbacks
    // -------------------------------------------------------------------------

    /** Called when saving changes to existing entity */
    onUpdate?: (model: T) => Promise<void>;

    /** Called when creating new entity */
    onCreate?: (model: T) => Promise<void>;

    /** Called when deleting entity */
    onDelete?: () => Promise<void>;

    // -------------------------------------------------------------------------
    // Lifecycle Callbacks
    // -------------------------------------------------------------------------

    /** Called after model is loaded */
    afterLoad?: () => Promise<void>;

    /** Called when mode changes */
    onModeChange?: (mode: EntityViewMode) => void;

    /**
     * Called when Cancel is clicked in edit/new mode.
     * If provided, prevents default view-mode switch and allows custom handling (e.g., close pane).
     */
    onCancel?: () => void;

    /**
     * Called when Cancel is clicked specifically in new mode.
     * Unlike onCancel, this only fires in new mode and does not affect edit-mode cancel.
     * If provided, prevents the default (empty) view-mode switch.
     */
    onNewCancel?: () => void;

    /**
     * Called when any form field value changes.
     */
    onFieldChange?: (key: string, value: unknown) => void;

    // -------------------------------------------------------------------------
    // Behavior Options
    // -------------------------------------------------------------------------

    /**
     * Show confirmation dialog when leaving dirty form.
     * Default: true
     */
    confirmDirtyState?: boolean;

    /**
     * Who asks the "unsaved changes, discard?" question when `confirmDirtyState`
     * applies. The component has no shell reference, so a host that has one
     * (entity panes do) supplies `() => shell.confirm(...)` here; without it the
     * view falls back to `window.confirm`. Resolve true to discard.
     */
    confirmDiscard?: () => Promise<boolean>;

    /**
     * Hide the form header (title + action buttons).
     * Useful when the pane provides its own send/cancel controls.
     * Default: false
     */
    hideFormHeader?: boolean;

    /**
     * Buttons to render in a header above the view-mode content, in the same
     * position as the form header. Lets a display-only view carry its own
     * in-pane controls — e.g. an "Edit" button that switches to edit mode —
     * rather than relying on a separate/global action bar. Re-invoked on every
     * view render; return [] (or omit) for no header.
     */
    viewActions?: () => HTMLElement[];

    // -------------------------------------------------------------------------
    // Accessibility
    // -------------------------------------------------------------------------

    /** ARIA label for the entity view container */
    ariaLabel: string;
}

// ============================================================================
// Supporting Types
// ============================================================================

/**
 * File information for file display/upload fields.
 */
export interface FileInfo {
    /** Unique file identifier */
    id: string;
    /** Original file name */
    name: string;
    /** File size in bytes */
    size: number;
    /** MIME type */
    mimeType: string;
    /** URL for download/preview */
    url?: string;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if a layout element is a header.
 */
export function isLayoutHeader<T>(element: LayoutElement<T>): element is LayoutHeader {
    return typeof element === 'object' && 'header' in element;
}

/**
 * Check if a layout element is a custom render function.
 */
export function isLayoutCustom<T>(element: LayoutElement<T>): element is LayoutCustom<T> {
    return typeof element === 'object' && 'custom' in element;
}

/**
 * Check if a layout element is a collapsible section.
 */
export function isLayoutSection<T>(element: LayoutElement<T>): element is LayoutSection<T> {
    return typeof element === 'object' && 'section' in element;
}

/**
 * Check if a layout element is a row of field keys.
 */
export function isLayoutRow<T>(element: LayoutElement<T>): element is LayoutRow<T> {
    return Array.isArray(element);
}
